import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EmailConfirmation = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('E-Mail wird bestätigt...');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Get the current session after email confirmation
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          setStatus('error');
          setMessage('Fehler bei der E-Mail-Bestätigung.');
          return;
        }

        if (session) {
          // Check if user has organization_id in profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('organization_id, team_id, onboarding_completed')
            .eq('user_id', session.user.id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            // Profile doesn't exist - this user needs onboarding
          setStatus('success');
          setMessage('E-Mail erfolgreich bestätigt! Sie werden zum Onboarding weitergeleitet...');
          setTimeout(() => {
            navigate('/onboarding');
          }, 500);
            return;
          }

          setStatus('success');
          setMessage('E-Mail erfolgreich bestätigt! Sie werden weitergeleitet...');
          
          // Redirect based on onboarding status
          setTimeout(() => {
            if (!profile?.onboarding_completed || (!profile?.organization_id && !profile?.team_id)) {
              // User hasn't completed onboarding -> go to onboarding
              navigate('/onboarding');
            } else {
              // User has completed onboarding -> go to home
              navigate('/');
            }
          }, 500);
        } else {
          setStatus('error');
          setMessage('Keine aktive Sitzung gefunden.');
        }
      } catch (error) {
        console.error('Unexpected error:', error);
        setStatus('error');
        setMessage('Ein unerwarteter Fehler ist aufgetreten.');
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-background/95">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <h2 className="text-2xl font-bold">E-Mail wird bestätigt</h2>
                <p className="text-muted-foreground">{message}</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="h-12 w-12 text-green-500" />
                <h2 className="text-2xl font-bold">E-Mail bestätigt!</h2>
                <p className="text-muted-foreground">{message}</p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-12 w-12 text-destructive" />
                <h2 className="text-2xl font-bold">Fehler</h2>
                <p className="text-muted-foreground">{message}</p>
                <Button onClick={() => navigate('/auth')}>
                  Zum Login
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailConfirmation;
